import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useSettings, whatsappLink } from "@/context/SettingsContext";
import { MessageSquare, X, Send, MessageCircle } from "lucide-react";

export default function Chatbot() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Hi! I'm the ${settings.business_name || "Taanya Homefoods"} assistant 🍫 Ask me about our products, prices, customisation, ordering, payment or delivery.`,
    },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const history = next.slice(-8).map((m) => ({ role: m.role, content: m.content }));
      const { data } = await axios.post(`${API}/chat`, { message: text, history });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong. Please try again or reach us on WhatsApp." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = ["What products do you have?", "How do I order?", "Payment & delivery info"];

  return (
    <>
      {!open && (
        <button
          data-testid="chatbot-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white shadow-lg transition-transform hover:scale-110"
          aria-label="Open chat"
        >
          <MessageSquare className="h-7 w-7" />
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-6 right-4 z-50 flex h-[70vh] max-h-[560px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-3xl border border-brand-line bg-cream shadow-2xl sm:right-6"
        >
          <div className="flex items-center justify-between bg-brand px-4 py-3 text-white">
            <div className="flex items-center gap-3">
              {settings.logo && <img src={settings.logo} alt="logo" className="h-9 w-9 rounded-full object-contain" />}
              <div className="leading-tight">
                <p className="font-heading text-lg">{settings.business_name || "Taanya Homefoods"}</p>
                <p className="text-xs text-white/80">Ask me anything</p>
              </div>
            </div>
            <button data-testid="chatbot-close" onClick={() => setOpen(false)} className="rounded-full p-1.5 hover:bg-white/15">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm ${
                    m.role === "user"
                      ? "rounded-br-sm bg-brand text-white"
                      : "rounded-bl-sm border border-brand-line bg-white text-brand-dark"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm border border-brand-line bg-white px-4 py-3">
                  <span className="flex gap-1">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-brand [animation-delay:-0.3s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-brand [animation-delay:-0.15s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-brand" />
                  </span>
                </div>
              </div>
            )}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    data-testid={`chat-suggestion`}
                    onClick={() => setInput(s)}
                    className="rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs text-brand-dark hover:bg-brand-gold-light"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-brand-line bg-white p-3">
            <a
              data-testid="chatbot-whatsapp"
              href={whatsappLink(settings.whatsapp, `Hi ${settings.business_name || "Taanya Homefoods"}, I have a question.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-2 flex items-center justify-center gap-2 rounded-full bg-[#25D366] py-2 text-sm font-medium text-white hover:bg-[#1EBE5A]"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
            <div className="flex items-center gap-2">
              <input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type your question..."
                className="flex-1 rounded-full border border-brand-line px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand-gold"
              />
              <button
                data-testid="chatbot-send"
                onClick={send}
                disabled={loading || !input.trim()}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
